import React, { useRef, useState, useEffect } from "react";
import {
  Popover,
  Button,
  Group,
  Text,
  createStyles,
  UnstyledButton,
  Flex,
  TextInput,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import moment from "moment";
import { MdOutlineCalendarToday } from "react-icons/md";
import { IoIosAdd } from "react-icons/io";
import { RxCross2 } from "react-icons/rx";
import swal from "sweetalert";

/* ========================= styles ========================= */
const useStyles = createStyles((theme) => ({
  tabButton: {
    height: 56,
    width: 160,
    padding: "8px 5px",
    color: "var(--app-text-muted)",
    fontFamily: "Inter",
    fontSize: 14,
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderBottom: "2px solid transparent",
    cursor: "pointer",
  },
  disabledTab: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  activeTab: {
    color: "var(--app-text)",
    borderBottom: "2px solid var(--app-text)",
  },
  buttonStyle: {
    fontFamily: "Inter",
    backgroundColor: "var(--app-surface)",
    borderRadius: "8px",
    border: "1px solid var(--app-border)",
    boxShadow: "0 1px 2px 0 rgba(16, 24, 40, 0.05), 0 0 0 4px var(--app-border)",
    height: "44px",
    padding: "10px 16px",
    color: "var(--app-text)",
    fontSize: 14,
    fontWeight: 600,
    marginTop: "6px",
    width: "100%",
    "&:hover": { backgroundColor: "var(--app-surface)" },
    "& > div": { justifyContent: "flex-start" },
  },
  popOverCard: {
    boxShadow: "4px 12px 24px 0 rgba(0, 0, 0, 0.10)",
    backgroundColor: "var(--app-surface)",
    borderRadius: "8px",
    padding: 14,
    border: "none",
  },
  layout: {
    display: "flex",
    gap: 16,
    "@media (max-width:834px)": { display: "block" },
  },
  leftRail: {
    borderRight: "1px solid var(--app-border)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    width: "fit-content",
    paddingRight: 14,
    gap: 12,
    "@media (max-width:834px)": { borderRight: "none", paddingRight: 0 },
  },
  rightSide: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    padding: "0px 8px 0px 22px",
  },
  railButton: {
    width: "fit-content",
    transition: "0.2s",
    color: "var(--app-text)",
    fontSize: 12,
    fontFamily: "Inter",
    fontWeight: 500,
    backgroundColor: "transparent",
    borderRadius: 5,
    height: "fit-content",
    padding: "8px 10px",
    "& .addIcon": {
      width: 18,
      height: 18,
      borderRadius: "50%",
      boxShadow: "0 0 4px 0 rgba(0, 0, 0, 0.25)",
      backgroundColor: "var(--app-surface)",
    },
    "&:hover": { backgroundColor: "var(--app-surface-2)" },
    "& .mantine-Button-label": { gap: 6, overflow: "unset" },
  },
  activeRailButton: {
    backgroundColor: "var(--app-surface-2)",
  },
  bottomBar: {
    borderTop: "1px solid var(--app-border)",
    paddingTop: 10,
    display: "flex",
    gap: 15,
  },
  cancelButton: {
    width: "fit-content",
    height: 44,
    borderRadius: 5,
    border: "0.703px solid var(--app-border)",
    background: "var(--app-surface-2)",
    boxShadow: "0 0.703px 1.406px 0 rgba(16, 24, 40, 0.05)",
    fontSize: 14,
    fontWeight: 600,
    color: "var(--app-text)",
    fontFamily: "Inter",
    "&:hover": { backgroundColor: "var(--app-surface-2)" },
  },
  darkButton: {
    height: 44,
    color: "var(--app-on-primary)",
    borderRadius: 5,
    border: "0.703px solid var(--app-primary)",
    background: "var(--app-primary)",
    boxShadow: "0 0.703px 1.406px 0 rgba(16, 24, 40, 0.05)",
    fontSize: 14,
    fontWeight: 600,
    fontFamily: "Inter",
    flex: 1,
    "&:hover": { backgroundColor: "var(--app-primary)" },
  },
  iconCalendar: { margin: "0 10px 0 0", color: "var(--app-text)", fontSize: 20 },
  chip: {
    fontSize: 12,
    fontFamily: "Inter",
    fontWeight: 500,
    color: "var(--app-text)",
    textAlign: "center",
    padding: 7,
    borderRadius: 4,
    border: "1.803px solid var(--app-border)",
    background: "var(--app-surface)",
  },
  titleInput: {
    width: 90,
    height: 33,
    padding: 7,
    textAlign: "center",
    borderRadius: 4,
    border: "1.803px solid var(--app-border)",
    fontSize: 11,
    fontFamily: "Inter",
    fontWeight: 500,
    color: "var(--app-text)",
    "&:focus": { border: "1.803px solid var(--app-text)" },
  },
}));

/* ========================= utils ========================= */
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const endOfDay = (d) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
const addDays = (d, days) => {
  const x = new Date(d);
  x.setDate(x.getDate() + days);
  return x;
};
const formatRange = (start, end) =>
  `${moment(start).format("MMM D, YYYY")} – ${moment(end).format(
    "MMM D, YYYY"
  )}`;

const ServiceAndReassessDatePicker = ({
  onSave,
  savedDates,
  edit,
  editReassessDate,
  disabled,
}) => {
  const { classes, cx } = useStyles();

  const [opened, setOpened] = useState(false);

  // tabs
  const [activeTab, setActiveTab] = useState("service"); // 'service' | 'reassess'

  // -------- Service Dates --------
  const [activeServicePreset, setActiveServicePreset] = useState("Custom");
  const [serviceRange, setServiceRange] = useState({ start: null, end: null });

  // -------- Reassess Dates --------
  const [activeReassessPreset, setActiveReassessPreset] = useState("Custom");
  const [reassessCustomDraft, setReassessCustomDraft] = useState([null, null]); // temp picker selection
  const [periods, setPeriods] = useState([]); // final list (30/60/90/custom)
  const [localIdCounter, setLocalIdCounter] = useState(1);

  const customInputRef = useRef(null);

  useEffect(() => {
    if (opened) {
      if (edit === "reassess") {
        setActiveTab("reassess");
      } else {
        setActiveTab("service");
      }
    }
  }, [opened, edit]);

  useEffect(() => {
    // service date prefill
    if (savedDates?.service?.start && savedDates?.service?.end) {
      setServiceRange({
        start: new Date(savedDates.service.start),
        end: new Date(savedDates.service.end),
      });
    }

    // reassess dates list prefill (always take full list)
    if (savedDates?.reassess?.length > 0) {
      setPeriods(
        savedDates.reassess.map((r) => ({
          ...r,
          start: new Date(r.start),
          end: new Date(r.end),
          label: r?.label ? r.label : formatRange(r.start, r.end),
        }))
      );
    } else {
      setPeriods([]);
    }
  }, [savedDates, opened, edit]);

  /* ---------- SERVICE: Presets ---------- */
  const selectServicePreset = (key) => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    let start = null;
    let end = null;

    if (key === "6 Month") {
      start = startOfDay(startOfMonth);
      end = endOfDay(
        new Date(startOfMonth.getFullYear(), startOfMonth.getMonth() + 6, 0)
      );
    } else if (key === "12 Month") {
      start = startOfDay(startOfMonth);
      end = endOfDay(
        new Date(startOfMonth.getFullYear(), startOfMonth.getMonth() + 12, 0)
      );
    } else if (key === "18 Month") {
      start = startOfDay(startOfMonth);
      end = endOfDay(
        new Date(startOfMonth.getFullYear(), startOfMonth.getMonth() + 18, 0)
      );
    } else if (key === "24 Month") {
      start = startOfDay(startOfMonth);
      end = endOfDay(
        new Date(startOfMonth.getFullYear(), startOfMonth.getMonth() + 24, 0)
      );
    } else if (key === "Date till Year") {
      start = startOfDay(now); // today se
      end = endOfDay(new Date(now.getFullYear(), 11, 31)); // year end
    } else if (key === "Date to Quarter") {
      start = startOfDay(now); // today se
      const currentQuarter = Math.floor(now.getMonth() / 3);
      const quarterEndMonth = currentQuarter * 3 + 2;
      const quarterEnd = new Date(now.getFullYear(), quarterEndMonth + 1, 0);
      end = endOfDay(quarterEnd); // quarter end
    }

    setActiveServicePreset(key);
    if (key === "Custom") {
      setServiceRange({ start: null, end: null });
    } else {
      setServiceRange({ start, end });
    }
  };

  const onServicePickerChange = (val) => {
    if (activeServicePreset !== "Custom") return;
    const [s, e] = val || [];
    setServiceRange({
      start: s ? startOfDay(s) : null,
      end: e ? endOfDay(e) : null,
    });
  };

  const canProceedToReassess = Boolean(serviceRange.start && serviceRange.end);

  /* ---------- REASSESS: add presets ---------- */
  const hasType = (type) => periods.some((p) => p.type === type);
  const addReassessPreset = (days) => {
    if (!serviceRange.start || !serviceRange.end) return;

    const start = startOfDay(serviceRange.start);
    const end = endOfDay(addDays(start, days - 1));

    if (end > serviceRange.end) {
      swal({
        title: "Invalid Reassessment Date Range",
        text: `The reassessment date range (${moment(start).format(
          "MMM D"
        )} – ${moment(end).format("MMM D")})
     extends beyond the selected service date range (${moment(
       serviceRange.start
     ).format("MMM D")} – ${moment(serviceRange.end).format(
          "MMM D"
        )}).Please choose a reassessment date range that falls within the service date range.`,
        icon: "error",
        className: classes.swalTextCenter,
      });
      return;
    }

    const type = `${days} Days`;
    if (hasType(type)) return; // prevent exact type duplicate

    const newPeriod = {
      type,
      title: type,
      start,
      end,
      label: formatRange(start, end),
      _id: `${localIdCounter}`,
    };
    setPeriods((prev) => [...prev, newPeriod]);
    setLocalIdCounter((c) => c + 1);
  };

  /* ---------- REASSESS: custom add ---------- */
  const addReassessCustom = (val) => {
    const [s, e] = val || [];
    if (!s || !e || !serviceRange.start || !serviceRange.end) return;

    const start = startOfDay(s);
    const end = endOfDay(e);

    if (start < serviceRange.start || end > serviceRange.end) {
      swal({
        title: "Invalid Reassessment Date Range",
        text: `The reassessment date range (${moment(start).format(
          "MMM D"
        )} – ${moment(end).format("MMM D")})
     extends beyond the selected service date range (${moment(
       serviceRange.start
     ).format("MMM D")} – ${moment(serviceRange.end).format(
          "MMM D"
        )}).Please choose a reassessment date range that falls within the service date range.`,
        icon: "error",
        className: classes.swalTextCenter,
      });
      return;
    }

    const label = formatRange(start, end);

    // 🔹 check duplicates
    const duplicate = periods.some(
      (p) =>
        p.type === "Custom" &&
        p.label === label &&
        (!p.title?.trim() || !p.title?.trim().toLowerCase()) // means title same ya empty
    );
    if (duplicate) {
      return;
    }

    // 🔹 auto title generate
    let title = "";
    const existingCustoms = periods.filter((p) => p.type === "Custom");
    const count = existingCustoms.length + 1;
    title = `Custom ${count > 1 ? count : ""}`.trim();

    const newPeriod = {
      type: "Custom",
      title, // auto title at insert time
      start,
      end,
      label,
      _id: `${localIdCounter}`,
    };

    setPeriods((prev) => [...prev, newPeriod]);
    setLocalIdCounter((c) => c + 1);

    setTimeout(() => {
      customInputRef.current?.focus();
    }, 0);

    setReassessCustomDraft([null, null]);
  };

  const onReassessPickerChange = (val) => {
    if (activeReassessPreset !== "Custom") return;
    setReassessCustomDraft(val || [null, null]);

    if (val?.[0] && val?.[1]) {
      addReassessCustom(val); // ✅ pass val directly
    }
  };

  /* ---------- Shared helpers ---------- */
  const updateCustomTitle = (_id, value) => {
    setPeriods((prev) =>
      prev.map((p) => (p._id === _id ? { ...p, title: value } : p))
    );
  };
  const removePeriod = (_id) =>
    setPeriods((prev) => prev.filter((p) => p._id !== _id));

  /* ---------- Save / Cancel / Next ---------- */
  const handleCancel = () => {
    setActiveServicePreset("Custom");
    setServiceRange({ start: null, end: null });

    setActiveReassessPreset("Custom");
    setReassessCustomDraft([null, null]);
    setPeriods([]);

    setActiveTab("service");
    setOpened(false);
  };

  const handleNext = () => {
    if (!canProceedToReassess) return;
    setActiveTab("reassess");
    setActiveReassessPreset("Custom");
    setReassessCustomDraft([null, null]);
  };

  const handleSave = () => {
    let customCounter = 1;
    const normalized = periods.map((p) => {
      if (p.type === "Custom") {
        let title = p.title?.trim();
        if (!title) {
          title = `Custom ${customCounter > 1 ? customCounter : ""}`.trim();
        }
        customCounter++;
        return { ...p, title };
      }
      return p;
    });

    const dataToSave = {
      service: { start: serviceRange.start, end: serviceRange.end },
      reassess: normalized,
    };

    if (onSave) onSave(dataToSave);

    handleCancel();
  };

  const servicePickerValue =
    serviceRange.start && serviceRange.end
      ? [serviceRange.start, serviceRange.end]
      : [];
  const reassessPickerValue = reassessCustomDraft;

  console.log(savedDates, servicePickerValue, periods);

  return (
    <Group justify="end">
      <Popover
        shadow="md"
        position="bottom-start"
        opened={opened}
        onClose={handleCancel}
        onChange={setOpened}
        disabled={disabled}
        sx={{ cursor: disabled ? "auto" : "default" }}
      >
        <Popover.Target>
          <Button
            className={classes.buttonStyle}
            onClick={() => setOpened((o) => !o)}
          >
            <Flex direction="column" align="flex-start">
              <Flex align="center">
                <MdOutlineCalendarToday className={classes.iconCalendar} />
                <Text>
                  {edit === "reassess" &&
                  editReassessDate?.start &&
                  editReassessDate?.end
                    ? `${moment(editReassessDate?.start).format(
                        "MMM D, YYYY"
                      )} – ${moment(editReassessDate?.end).format(
                        "MMM D, YYYY"
                      )}`
                    : savedDates?.service?.start && savedDates?.service?.end
                    ? `${moment(savedDates?.service?.start).format(
                        "MMM D, YYYY"
                      )} – ${moment(savedDates?.service?.end).format(
                        "MMM D, YYYY"
                      )}`
                    : "Select Period"}
                </Text>
              </Flex>
            </Flex>
          </Button>
        </Popover.Target>

        <Popover.Dropdown className={classes.popOverCard}>
          <div className={classes.layout}>
            {/* ----------------- Left rail ----------------- */}
            <div className={classes.leftRail}>
              {activeTab === "service" ? (
                <>
                  {[
                    "6 Month",
                    "12 Month",
                    "18 Month",
                    "24 Month",
                    "Date till Year",
                    "Date to Quarter",
                    "Custom",
                  ].map((key) => (
                    <Button
                      key={key}
                      className={cx(classes.railButton, {
                        [classes.activeRailButton]: activeServicePreset === key,
                      })}
                      onClick={() => selectServicePreset(key)}
                    >
                      {key}
                    </Button>
                  ))}
                </>
              ) : (
                <>
                  {[30, 60, 90].map((d) => (
                    <Button
                      key={d}
                      className={cx(classes.railButton, {
                        [classes.activeRailButton]:
                          activeReassessPreset === `${d} Days`,
                      })}
                      onClick={() => {
                        setActiveReassessPreset(`${d} Days`);
                        addReassessPreset(d);
                      }}
                      disabled={!serviceRange.start || hasType(`${d} Days`)}
                    >
                      {d} Days
                      <Flex className="addIcon" align="center" justify="center">
                        <IoIosAdd size={15} />
                      </Flex>
                    </Button>
                  ))}
                  <Button
                    className={cx(classes.railButton, {
                      [classes.activeRailButton]:
                        activeReassessPreset === "Custom",
                    })}
                    onClick={() => {
                      setActiveReassessPreset("Custom");
                    }}
                    disabled={!serviceRange.start}
                  >
                    Custom
                    <Flex className="addIcon" align="center" justify="center">
                      <IoIosAdd size={15} />
                    </Flex>
                  </Button>
                </>
              )}
            </div>

            {/* ----------------- Right side ----------------- */}
            <div className={classes.rightSide}>
              {/* Tabs header */}
              {edit !== "reassess" && (
                <Flex style={{ width: "100%" }} align="center" justify="center">
                  <UnstyledButton
                    className={cx(classes.tabButton, {
                      [classes.activeTab]: activeTab === "service",
                    })}
                    onClick={() => setActiveTab("service")}
                  >
                    Service Dates
                  </UnstyledButton>

                  <UnstyledButton
                    className={cx(classes.tabButton, {
                      [classes.activeTab]: activeTab === "reassess",
                      [classes.disabledTab]: !canProceedToReassess,
                    })}
                    onClick={() => {
                      if (canProceedToReassess) setActiveTab("reassess");
                    }}
                  >
                    Reassess Dates
                  </UnstyledButton>
                </Flex>
              )}

              {/* Calendar */}
              {activeTab === "service" ? (
                <DatePicker
                  type="range"
                  value={servicePickerValue}
                  onChange={onServicePickerChange}
                  mx="auto"
                  sx={{
                    pointerEvents:
                      activeServicePreset === "Custom" ? "auto" : "none",
                    opacity: activeServicePreset === "Custom" ? 1 : 0.7,
                  }}
                  placeholder="Select date range"
                />
              ) : (
                <DatePicker
                  type="range"
                  value={reassessPickerValue}
                  onChange={onReassessPickerChange}
                  minDate={serviceRange.start || undefined}
                  maxDate={serviceRange.end || undefined}
                  mx="auto"
                  sx={{
                    pointerEvents:
                      activeReassessPreset === "Custom" ? "auto" : "none",
                    opacity: activeReassessPreset === "Custom" ? 1 : 0.7,
                  }}
                  placeholder="Select reassess range"
                />
              )}

              {/* Selected readouts / lists */}
              {activeTab === "service"
                ? serviceRange?.start &&
                  serviceRange?.end && (
                    <Flex
                      gap={10}
                      pt={12}
                      align="center"
                      sx={{ borderTop: "1px solid var(--app-border)" }}
                    >
                      <Text className={classes.chip}>
                        {serviceRange.start
                          ? moment(serviceRange.start).format("MMMM D, YYYY")
                          : "—"}
                      </Text>
                      <Text>—</Text>
                      <Text className={classes.chip}>
                        {serviceRange.end
                          ? moment(serviceRange.end).format("MMMM D, YYYY")
                          : "—"}
                      </Text>
                    </Flex>
                  )
                : periods.length > 0 && (
                    <Flex
                      direction="column"
                      gap={16}
                      pt={12}
                      sx={{ borderTop: "1px solid var(--app-border)" }}
                    >
                      {periods.map((p, idx) => (
                        <Flex
                          key={`${p.type}-${p.label}-${idx}`}
                          align="center"
                          gap="10px"
                        >
                          {p.type === "Custom" ? (
                            <>
                              <TextInput
                                ref={customInputRef}
                                placeholder="Title"
                                value={p.title}
                                mr={10}
                                classNames={{ input: classes.titleInput }}
                                onChange={(e) =>
                                  updateCustomTitle(
                                    p._id,
                                    e.currentTarget.value
                                  )
                                }
                              />
                              <Text className={classes.chip}>{p.label}</Text>
                            </>
                          ) : (
                            <>
                              <Text
                                fz={11}
                                mr={10}
                                fw={500}
                                ff="Inter"
                                color="var(--app-text)"
                                miw={75}
                                ta="center"
                              >
                                {p.type}
                              </Text>
                              <Text className={classes.chip}>{p.label}</Text>
                            </>
                          )}
                          <UnstyledButton
                            onClick={() => removePeriod(p._id)}
                            style={{ marginLeft: "auto" }}
                          >
                            <RxCross2 size={15} color="var(--app-text)" />
                          </UnstyledButton>
                        </Flex>
                      ))}
                    </Flex>
                  )}

              {/* Bottom actions */}
              <div className={classes.bottomBar}>
                <Button className={classes.cancelButton} onClick={handleCancel}>
                  Cancel
                </Button>

                {activeTab === "service" ? (
                  <Button
                    className={classes.darkButton}
                    onClick={handleNext}
                    disabled={!canProceedToReassess}
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    className={classes.darkButton}
                    onClick={handleSave}
                    disabled={periods.length === 0}
                  >
                    Save
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Popover.Dropdown>
      </Popover>
    </Group>
  );
};

export default ServiceAndReassessDatePicker;
